// Paginated table of scanned URLs that haven't been evaluated yet.
// Parent owns filter state. Each row has checkbox + Evaluate button.
// Data from GET /api/matches/pending.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, X } from 'lucide-react';
import { apiDelete, apiGet } from '../../lib/api';
import { useZh } from '../../lib/useProduct';
import type { PendingResponse, PendingRow } from '../../lib/types';

const PER_PAGE = 10;

export interface PendingFilters {
  sources: string[];            // [] = all
  postedWithinHours: number | null;
  q: string;
  withSalary: boolean;
  country?: string;             // e.g. "UK" — matched against city+state heuristically
  // Free-form scan search term ("AI/ML Lecturer; Postdoc"). Server
  // tokenizes it and drops rows whose title matches NONE of the
  // tokens. Empty/undefined → no post-filter applied.
  keywords?: string;
  // Sort key; one of: first_seen | salary | company | title.
  // Backend validates and falls back to first_seen for unknown values.
  sort?: string;
}

function expandSources(keys: string[]): string | undefined {
  if (!keys.length) return undefined;
  const expanded = new Set<string>();
  for (const k of keys) {
    if (k === 'api') {
      ['greenhouse-api', 'ashby-api', 'lever-api', 'workable-api'].forEach((s) => expanded.add(s));
    } else {
      expanded.add(k);
    }
  }
  return Array.from(expanded).join(',');
}

function siteBadgeColor(site: string): string {
  if (/greenhouse/.test(site)) return 'var(--line2)';
  if (/ashby/.test(site)) return '#ece4f5';
  if (/lever/.test(site)) return '#f4e7db';
  if (/workable/.test(site)) return '#e8f0e8';
  if (site === 'linkedin') return '#dbe7f4';
  if (site === 'indeed') return '#dcefd8';
  if (site === 'glassdoor') return '#f1e1cf';
  if (site === 'zip_recruiter') return '#f4d8d8';
  return 'var(--line2)';
}

function siteDisplay(site: string): string {
  if (/-api$/.test(site)) return site.replace('-api', '');
  if (site === 'zip_recruiter') return 'ZipRecruiter';
  return site;
}

function fmtRelative(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 14) return `${d}d ago`;
  return iso.slice(0, 10);
}

function fmtSalary(row: PendingRow): string {
  if (row.min_amount == null && row.max_amount == null) return '—';
  const cur = row.interval === 'hourly' ? '/hr' : '/yr';
  const fmt = (n: number) => (n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${n}`);
  if (row.min_amount != null && row.max_amount != null) {
    return `${fmt(row.min_amount)}–${fmt(row.max_amount)}${cur}`;
  }
  return `${fmt((row.min_amount ?? row.max_amount) as number)}${cur}`;
}

interface Props {
  filters: PendingFilters;
  refreshKey?: number;
  runningUrls: Set<string>;          // URLs already queued for evaluation
  onEvaluate: (rows: PendingRow[]) => void;
}

export default function PendingJobsTable({ filters, refreshKey = 0, runningUrls, onEvaluate }: Props) {
  const zh = useZh();
  const [page, setPage] = useState(0);
  // Selection survives pagination: keeping a Map<url, PendingRow> means we
  // retain the row data needed for Evaluate even when the user has already
  // paged away from where they ticked the checkbox. Filter changes still
  // clear the cart to avoid evaluating rows the user can no longer see.
  const [selected, setSelected] = useState<Map<string, PendingRow>>(new Map());
  // Armed delete: first × click ARMS the row (red highlight + confirm copy),
  // second click within 4s actually deletes. Beats native confirm() for keyboard
  // flow and matches the pattern used elsewhere in the app.
  const [armedDeleteUrl, setArmedDeleteUrl] = useState<string | null>(null);
  const [deletingUrl, setDeletingUrl] = useState<string | null>(null);
  const qc = useQueryClient();

  // Auto-disarm after 4s if user doesn't follow through
  useEffect(() => {
    if (!armedDeleteUrl) return;
    const t = setTimeout(() => setArmedDeleteUrl(null), 4000);
    return () => clearTimeout(t);
  }, [armedDeleteUrl]);

  const deleteRow = async (url: string) => {
    setDeletingUrl(url);
    setArmedDeleteUrl(null);
    try {
      await apiDelete(`/api/scan-history/by-url?url=${encodeURIComponent(url)}`);
      // Drop from local selection if present
      setSelected((cur) => {
        if (!cur.has(url)) return cur;
        const next = new Map(cur);
        next.delete(url);
        return next;
      });
      // Refetch this query + the matches summary so dashboard counts update.
      // The 'matches-pending' invalidation is prefix-match so it ALSO hits
      // the tab-badge count query at ['matches-pending', 'count', ...] —
      // see Matches.tsx for the rationale.
      qc.invalidateQueries({ queryKey: ['matches-pending'] });
      qc.invalidateQueries({ queryKey: ['matches'] });
    } catch (e) {
      alert(`Could not delete: ${(e as Error).message}`);
    } finally {
      setDeletingUrl(null);
    }
  };

  useEffect(() => {
    setPage(0);
    setSelected(new Map());
    setArmedDeleteUrl(null);
  }, [filters]);

  const params = useMemo(
    () => ({
      limit: PER_PAGE,
      offset: page * PER_PAGE,
      sources: expandSources(filters.sources || []),
      posted_within_hours: filters.postedWithinHours ?? undefined,
      q: (filters.q || '').trim() || undefined,
      with_salary: filters.withSalary ? '1' : undefined,
      country: filters.country || undefined,
      keywords: (filters.keywords || '').trim() || undefined,
      sort: filters.sort || undefined,
    }),
    [filters, page]
  );

  const result = useQuery({
    queryKey: ['matches-pending', params, refreshKey],
    queryFn: () =>
      apiGet<PendingResponse>(
        '/api/matches/pending',
        params as Record<string, string | number | boolean | undefined>
      ),
    staleTime: 15_000,
    // Auto-refresh while the worker drains the backlog (URLs leave Pending
    // as their reports land). 30s keeps it live without hammering.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
  });

  const items = result.data?.items ?? [];
  const total = result.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  const pageUrls = items.map((r) => r.url);
  const allPageSelected = pageUrls.length > 0 && pageUrls.every((u) => selected.has(u));

  const togglePageAll = () => {
    setSelected((cur) => {
      const next = new Map(cur);
      if (allPageSelected) {
        pageUrls.forEach((u) => next.delete(u));
      } else {
        items.forEach((r) => next.set(r.url, r));
      }
      return next;
    });
  };

  const toggleOne = (row: PendingRow) => {
    setSelected((cur) => {
      const next = new Map(cur);
      if (next.has(row.url)) next.delete(row.url);
      else next.set(row.url, row);
      return next;
    });
  };

  const evaluateSelected = () => {
    const rows = Array.from(selected.values()).filter((r) => !runningUrls.has(r.url));
    if (rows.length === 0) return;
    if (rows.length > 20) {
      const minutes = Math.ceil((rows.length * 60) / 2 / 60);
      const ok = confirm(
        `Queue ${rows.length} evaluations?\n\nEach takes ~60s, runs 2 at a time → roughly ${minutes} min total. You can close the browser; jobs keep running.`
      );
      if (!ok) return;
    }
    onEvaluate(rows);
    setSelected(new Map());
  };

  const evaluateOne = (row: PendingRow) => {
    if (runningUrls.has(row.url)) return;
    onEvaluate([row]);
  };

  // Count ALL selected rows across pages (minus those already running).
  const selectedCount = Array.from(selected.keys()).filter(
    (u) => !runningUrls.has(u)
  ).length;
  // Count only the ones on the current page, for showing page-specific hints.
  const pageSelectedCount = pageUrls.filter(
    (u) => selected.has(u) && !runningUrls.has(u)
  ).length;

  // Action column widened from 0.6fr → 0.9fr to fit the new × button.
  const gridCols = '28px 0.9fr 1fr 2.3fr 1fr 1fr 0.9fr';

  return (
    <div className="card">
      <div className="row-head" style={{ gridTemplateColumns: gridCols }}>
        <div className="tab" style={{ padding: 0, display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={allPageSelected}
            onChange={togglePageAll}
            style={{ accentColor: 'var(--pri)', cursor: 'pointer' }}
          />
        </div>
        {(zh
          ? ['扫描于', '来源', '公司 · 职位', '地点', '薪资', '']
          : ['Scanned', 'Source', 'Company · Role', 'Location', 'Salary', '']
        ).map((h, i) => (
          <div key={i} className="tab">{h}</div>
        ))}
      </div>

      {result.isLoading && <div className="empty">{zh ? '加载中…' : 'Loading pending roles…'}</div>}
      {result.isError && (
        <div className="empty">
          <span className="serif">Couldn't load pending roles.</span>
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink2)' }}>
            {(result.error as Error)?.message || 'Unknown error'}
          </div>
        </div>
      )}
      {!result.isLoading && !result.isError && items.length === 0 && (
        <div className="empty">
          <span className="serif">{zh ? "暂无待评估职位。" : "Nothing pending."}</span>
          {zh ? "运行一次扫描，或放宽筛选。" : "Run a scan, or relax filters."}
        </div>
      )}

      {items.map((row) => {
        const isRunning = runningUrls.has(row.url);
        return (
          <div
            key={row.id}
            className="row"
            style={{
              gridTemplateColumns: gridCols,
              opacity: isRunning ? 0.55 : 1,
              cursor: 'pointer',
            }}
            onClick={() => window.open(row.url, '_blank', 'noreferrer')}
            title={row.site === 'indeed' ? 'Indeed may require Cloudflare verification on first open' : row.url}
          >
            <div
              style={{ display: 'flex', alignItems: 'center', padding: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={selected.has(row.url)}
                disabled={isRunning}
                onChange={() => toggleOne(row)}
                style={{ accentColor: 'var(--pri)', cursor: isRunning ? 'not-allowed' : 'pointer' }}
              />
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{fmtRelative(row.first_seen)}</div>
            <div>
              <span
                className="chip"
                style={{ background: siteBadgeColor(row.site), fontSize: 10.5, padding: '2px 7px' }}
                title={row.site}
              >
                {siteDisplay(row.site)}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>{row.title}</div>
              <div style={{ fontSize: 12, color: 'var(--ink2)' }}>{row.company || '—'}</div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink2)' }}>
              {[row.city, row.state].filter(Boolean).join(', ') || '—'}
            </div>
            <div
              style={{
                fontSize: 12,
                color: row.min_amount != null ? 'var(--ink)' : 'var(--ink3)',
                fontWeight: row.min_amount != null ? 500 : 400,
              }}
            >
              {fmtSalary(row)}
            </div>
            <div
              style={{
                display: 'flex',
                gap: 6,
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="btn primary"
                disabled={isRunning}
                onClick={() => evaluateOne(row)}
                title={isRunning ? (zh ? '正在评估…' : 'Already evaluating…') : (zh ? '按你的画像评估这个职位' : 'Evaluate this role')}
                style={{ padding: '4px 10px', fontSize: 11 }}
              >
                {isRunning ? '…' : (zh ? '评估' : 'Evaluate')}
              </button>
              <button
                className="btn ghost"
                style={{ padding: '2px 5px', border: 'none' }}
                title="Open posting in new tab"
                onClick={() => window.open(row.url, '_blank', 'noreferrer')}
              >
                <ExternalLink size={13} color="var(--ink3)" />
              </button>
              {/* Armed delete: 1st click arms (red), 2nd click within 4s
                  actually removes the row from data/scan-history.tsv. */}
              <button
                className="btn ghost"
                disabled={isRunning || deletingUrl === row.url}
                style={{
                  padding: '2px 5px',
                  border: armedDeleteUrl === row.url ? '1px solid #b43a3a' : 'none',
                  color: armedDeleteUrl === row.url ? '#b43a3a' : 'var(--ink3)',
                  background: armedDeleteUrl === row.url ? 'rgba(180,58,58,0.06)' : undefined,
                  fontSize: 11,
                }}
                title={
                  armedDeleteUrl === row.url
                    ? 'Click again to confirm — removes this row from scan-history.tsv'
                    : 'Remove this row from your pending list (you can re-scan to bring it back)'
                }
                onClick={() => {
                  if (armedDeleteUrl === row.url) deleteRow(row.url);
                  else setArmedDeleteUrl(row.url);
                }}
              >
                {deletingUrl === row.url
                  ? '…'
                  : armedDeleteUrl === row.url
                    ? 'Confirm'
                    : <X size={13} />}
              </button>
            </div>
          </div>
        );
      })}

      {total > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '14px 20px',
            borderTop: '1px solid var(--line2)',
            fontSize: 12,
            gap: 10,
          }}
        >
          <span style={{ color: 'var(--ink3)' }}>
            Page <strong style={{ color: 'var(--ink)' }}>{page + 1}</strong> of{' '}
            <strong style={{ color: 'var(--ink)' }}>{pages}</strong> · {total.toLocaleString()} total
          </span>
          {selectedCount > 0 && (
            <>
              <button
                className="btn primary"
                style={{ marginLeft: 16, padding: '4px 12px' }}
                onClick={evaluateSelected}
                title={
                  selectedCount > pageSelectedCount
                    ? `${selectedCount} selected across pages (${pageSelectedCount} on this page). Queue them all.`
                    : `${selectedCount} selected on this page. Queue them.`
                }
              >
                Evaluate selected ({selectedCount})
              </button>
              <button
                className="btn ghost"
                style={{ padding: '4px 10px', fontSize: 11 }}
                onClick={() => setSelected(new Map())}
                title="Clear every checkbox across all pages"
              >
                Clear
              </button>
            </>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn" disabled={page === 0} onClick={() => setPage(page - 1)}>
              ← Prev
            </button>
            <button className="btn" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
